import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getLeads, updateLead, logCallAttempt, getPipelineStatuses, getCallOutcomes, getLeadCallQueue } from "@/lib/apiClient";
import { Phone, Search, ChevronRight, Mail, MapPin, Save, Headphones, Package, BookOpen, Users, CalendarPlus, History } from "lucide-react";
import { toast } from "sonner";
import LiveScript from "@/pages/LiveScript";
import { MatchingPackages } from "@/components/MatchingPackages";
import { CallHistory } from "@/components/CallHistory";
import { KbSearch } from "@/components/KbSearch";
import { AppointmentPanel } from "@/components/AppointmentPanel";
import { DialButton } from "@/components/DialButton";
import { FormSubmissions } from "@/components/FormSubmissions";
import { readBel, writeBel, readBelFor, writeBelFor } from "@/lib/belSession";

const CAT_LABEL = { verkoper: "Verkoper", verhuurder: "Verhuurder", koper: "Koper", huurder: "Huurder", partner: "Partner", overig: "Overig" };
const SCRIPT_FOR = { verkoper: "owners", verhuurder: "owners", koper: "seekers", huurder: "seekers", partner: "partners" };

function LeadPicker({ leads, currentId, onPick }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (t ? leads.filter((l) => `${l.name} ${l.phone} ${l.email}`.toLowerCase().includes(t)) : leads).slice(0, 60);
  }, [leads, q]);
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-4" data-testid="lead-picker">
      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8994A3]" />
        <input data-testid="lead-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek lead op naam of nummer…" className="fe-input pl-9" />
      </div>
      <div className="space-y-1 max-h-[60vh] overflow-y-auto fe-scroll">
        {shown.map((l) => (
          <button key={l.id} data-testid={`pick-lead-${l.id}`} onClick={() => onPick(l.id)}
            className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${l.id === currentId ? "bg-[#001C51] text-white" : "hover:bg-[#EDF1F5] text-[#16202B]"}`}>
            <span className="font-medium text-sm truncate">{l.name}</span>
            <span className={`ml-auto text-xs ${l.id === currentId ? "text-white/70" : "text-[#5B6675]"}`}>{CAT_LABEL[l.category] || ""}</span>
          </button>
        ))}
        {shown.length === 0 && <p className="text-sm text-[#5B6675] px-2">Geen leads gevonden.</p>}
      </div>
    </div>
  );
}

function LeadCard({ lead, pipeline, outcomes, onUpdated }) {
  const draft = () => readBelFor("draft", lead.id);
  const [notes, setNotes] = useState(draft()?.notes ?? (lead.notes || ""));
  const [follow, setFollow] = useState(draft()?.follow ?? (lead.follow_up_date || "").slice(0, 10));
  useEffect(() => { setNotes(draft()?.notes ?? (lead.notes || "")); setFollow(draft()?.follow ?? (lead.follow_up_date || "").slice(0, 10)); }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const dirty = notes !== (lead.notes || "") || follow !== (lead.follow_up_date || "").slice(0, 10);
    writeBelFor("draft", lead.id, dirty ? { notes, follow } : undefined);
  }, [notes, follow, lead.id, lead.notes, lead.follow_up_date]);

  const patch = async (data, msg) => {
    try { onUpdated(await updateLead(lead.id, data)); toast.success(msg); } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };
  const logOutcome = async (key) => {
    try { onUpdated(await logCallAttempt(lead.id, { outcome: key })); toast.success("Belpoging vastgelegd"); } catch { toast.error("Vastleggen mislukt"); }
  };

  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-5 space-y-4" data-testid="bel-lead-card">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-xl bg-[#001C51] text-white flex items-center justify-center font-heading font-bold text-lg shrink-0">{lead.name.charAt(0).toUpperCase()}</div>
        <div className="min-w-0">
          <h2 data-testid="bel-lead-name" className="font-heading font-semibold text-xl text-[#16202B] truncate">{lead.name}</h2>
          <p className="text-sm text-[#5B6675]">{CAT_LABEL[lead.category] || "Overig"}{lead.company ? ` · ${lead.company}` : ""}</p>
        </div>
      </div>
      {lead.phone ? <DialButton lead={lead} /> : <p data-testid="bel-no-phone" className="text-sm text-[#B0413E]">Geen telefoonnummer bekend.</p>}
      <div className="text-sm text-[#5B6675] space-y-1">
        {lead.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {lead.email}</p>}
        {lead.location && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {lead.location}</p>}
        <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {lead.call_attempts || 0} belpoging(en)</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-1.5">Pipeline</p>
        <select data-testid="bel-pipeline" value={lead.pipeline_status || "new"} onChange={(e) => patch({ pipeline_status: e.target.value }, "Status bijgewerkt")} className="fe-input">
          {pipeline.filter((s) => s.active).map((s) => <option key={s.id} value={s.key}>{s.label}</option>)}
        </select>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-1.5">Gespreksuitkomst vastleggen</p>
        <div className="flex flex-wrap gap-1.5">
          {outcomes.filter((o) => o.active).map((o) => (
            <button key={o.id} data-testid={`bel-outcome-${o.key}`} onClick={() => logOutcome(o.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${lead.call_outcome === o.key ? "bg-[#001C51] text-white border-[#001C51]" : "border-[#E2E7EC] text-[#5B6675] hover:border-[#001C51]"}`}>{o.label}</button>
          ))}
        </div>
      </div>
      {lead.asking_price && <p className="text-sm text-[#16202B]" data-testid="bel-asking-price"><span className="text-[#5B6675]">Vraagprijs/waarde:</span> {lead.asking_price}{lead.timeline ? ` · ${lead.timeline}` : ""}</p>}
      {lead.form_submissions?.length > 0 ? <FormSubmissions lead={lead} /> : lead.form_answers && (
        <div className="rounded-xl bg-[#F6F9FB] border border-[#E2E7EC] p-3">
          <p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-1">Formulierantwoorden</p>
          <pre className="text-xs text-[#16202B] whitespace-pre-wrap font-sans max-h-32 overflow-y-auto fe-scroll">{lead.form_answers}</pre>
        </div>
      )}
      <div>
        <p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-1.5">Notities</p>
        <textarea data-testid="bel-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} className="fe-input resize-y" placeholder="Wat is besproken…" />
        <div className="flex items-center gap-2 mt-2">
          <input type="date" data-testid="bel-followup" value={follow} onChange={(e) => setFollow(e.target.value)} className="fe-input" />
          <button data-testid="bel-save" onClick={() => patch({ notes, follow_up_date: follow || "" }, "Opgeslagen")} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A] shrink-0"><Save className="h-4 w-4" /> Opslaan</button>
        </div>
      </div>
    </div>
  );
}

const SIDE_TABS = [
  { id: "calls", label: "Gesprekken", icon: Headphones },
  { id: "appt", label: "Afspraak", icon: CalendarPlus },
  { id: "packages", label: "Pakketten", icon: Package },
  { id: "kb", label: "Kennisbank", icon: BookOpen },
  { id: "history", label: "Historie", icon: History },
];

function ActivityLog({ lead }) {
  const items = [...(lead.activity || [])].reverse();
  return (
    <div className="rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4" data-testid="bel-activity">
      <p className="text-sm font-semibold text-[#001C51] mb-3">Gedeelde historie</p>
      {items.length === 0 ? <p className="text-sm text-[#5B6675]">Nog geen activiteit.</p> : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto fe-scroll">
          {items.map((a, i) => (
            <div key={i} className="rounded-lg bg-white border border-[#E2E7EC] px-3 py-2">
              <p className="text-sm text-[#16202B]">{a.detail}</p>
              <p className="text-[11px] text-[#8994A3]">{a.by} · {new Date(a.at).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SideTabs({ tab, setTab }) {
  return (
    <nav className="flex flex-wrap gap-2 mb-5" data-testid="bel-side-tabs">
      {SIDE_TABS.map((t) => (
        <button key={t.id} data-testid={`bel-tab-${t.id}`} onClick={() => setTab(t.id)}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${tab === t.id ? "bg-[#001C51] text-white border-[#001C51]" : "bg-white text-[#001C51] border-[#E2E7EC] hover:bg-[#EDF1F5]"}`}>
          <t.icon className="h-4 w-4" /> {t.label}
        </button>
      ))}
    </nav>
  );
}

function SidePanel({ lead, tab }) {
  return (
    <div className="space-y-3" data-testid="bel-side-panel">
      {tab === "calls" && <CallHistory leadId={lead.id} />}
      {tab === "appt" && <AppointmentPanel lead={lead} />}
      {tab === "packages" && <MatchingPackages lead={lead} />}
      {tab === "history" && <ActivityLog lead={lead} />}
      {tab === "kb" && <div className="rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4"><KbSearch /></div>}
    </div>
  );
}

export default function Belomgeving() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [tab, setTabState] = useState(readBel().tab || "calls");
  const setTab = (t) => { setTabState(t); writeBel({ tab: t }); };

  useEffect(() => {
    if (leadId) writeBel({ leadId });
    else if (readBel().leadId) navigate(`/belomgeving/${readBel().leadId}`, { replace: true });
  }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getLeads().then(setLeads).catch(() => setLeads([]));
    getPipelineStatuses().then(setPipeline).catch(() => {});
    getCallOutcomes().then(setOutcomes).catch(() => {});
  }, []);

  const lead = leads?.find((l) => l.id === leadId);
  const idx = leads ? leads.findIndex((l) => l.id === leadId) : -1;
  const pick = (id) => navigate(`/belomgeving/${id}`);
  const nextFromQueue = async () => {
    try {
      const q = await getLeadCallQueue();
      const id = q.next_ids.find((x) => x !== leadId);
      if (id) pick(id); else toast.success("De bellijst is leeg — goed gedaan!");
    } catch { toast.error("Bellijst ophalen mislukt"); }
  };
  const onUpdated = (u) => setLeads((ls) => ls.map((l) => (l.id === u.id ? u : l)));

  return (
    <div className="px-4 lg:px-6 py-6 w-full fe-fade-up" data-testid="belomgeving">
      <header className="flex flex-wrap items-center gap-3 mb-4">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#16202B]">Belomgeving</h1>
          <p className="text-[#5B6675] mt-1 text-sm">Lead, script, pakketten, kennisbank en gesprekken op één scherm.</p>
        </div>
        {lead && (
          <div className="ml-auto flex items-center gap-2">
            {lead.assigned_user_name && <span data-testid="bel-assignee" className="text-xs px-2.5 py-1 rounded-full bg-[#E3E9F5] text-[#001C51]">Bellen: {lead.assigned_user_name}</span>}
            {lead.last_contact_name && <span data-testid="bel-contacted" className="text-xs px-2.5 py-1 rounded-full bg-[#D6F3F3] text-[#0C6E6E]">Gecontacteerd door {lead.last_contact_name} · {new Date(lead.last_contact_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}</span>}
            <button data-testid="bel-change-lead" onClick={() => { writeBel({ leadId: "" }); navigate("/belomgeving"); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E7EC] bg-white text-[#001C51] text-sm font-medium hover:bg-[#EDF1F5]"><Users className="h-4 w-4" /> Andere lead</button>
            <button data-testid="bel-next-lead" onClick={nextFromQueue} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A]">Volgende uit bellijst <ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </header>
      {lead && <SideTabs tab={tab} setTab={setTab} />}

      {leads === null ? <p className="text-[#5B6675]">Laden…</p>
        : !lead ? (
          <div className="max-w-xl">
            <p className="text-[#5B6675] mb-3" data-testid="bel-pick-hint">{leadId ? "Deze lead is niet gevonden of je hebt er geen toegang toe. " : ""}Kies een lead om te starten.</p>
            <LeadPicker leads={leads} currentId={leadId} onPick={pick} />
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
            <div className="xl:col-span-3"><LeadCard lead={lead} pipeline={pipeline} outcomes={outcomes} onUpdated={onUpdated} /></div>
            <div className="xl:col-span-5"><LiveScript embedded initialScriptId={SCRIPT_FOR[lead.category]} sessionKey={lead.id} /></div>
            <div className="xl:col-span-4"><SidePanel lead={lead} tab={tab} /></div>
          </div>
        )}
    </div>
  );
}
